import {useEffect} from 'react';
import {useRouter} from 'next/router';
import {Center} from '@chakra-ui/layout';
import {useBoolean, useDisclosure} from '@chakra-ui/hooks';
import {useToast} from '@chakra-ui/toast';

import {useTranslate} from '@eksab/i18n';
import {useMeQuery} from '@eksab/hooks/useMeQuery';
import {useRegionSettings} from '@eksab/hooks/useRegionSettings';
import {Loader, VerifyNumberToJoin} from '@eksab/components';

import {AuthModal} from '@eksab/features/auth/modals/AuthModal';
import {BuyCoinsModal} from '@eksab/features/competition/components/modals/BuyCoinsModal';
import {useChallengeQuery} from '@eksab/features/trivia/hooks/useChallengeQuery';
import type {Challenge} from '@eksab/features/trivia/types';

import {JoinModal} from './JoinModal';

export function JoinChallengeModal() {
  const toast = useToast();
  const router = useRouter();

  const t = useTranslate();

  const challengeID = router.query['challenge-id'] as string | undefined;

  const challengeQuery = useChallengeQuery(challengeID, {
    onSuccess: (challenge) => {
      if (challenge) return;

      onJoinChallengeModalClose();
      toast({title: t('trivia.challenge.not-available'), status: 'error'});
    },
    onError: (e) => {
      onJoinChallengeModalClose();
      toast({title: e.response?.data.message, status: 'error'});
    },
  });

  const onJoinChallengeModalClose = () => {
    delete router.query['challenge-id'];
    router.replace({query: router.query}, undefined, {shallow: true, scroll: false});
  };

  if (!challengeID) return null;

  if (challengeQuery.isLoading) return <LoadingOverlay />;

  if (challengeQuery.isError) return null;

  if (challengeQuery.data === null) return null;

  if (challengeQuery.data.available_for_guests)
    return <JoinModal challenge={challengeQuery.data} onClose={onJoinChallengeModalClose} />;

  return <AuthenticatedJoinFlow challenge={challengeQuery.data} />;
}

const LoadingOverlay = () => (
  <Center pos="fixed" inset="0" backdropFilter="auto" backdropBlur="sm">
    <Loader size="xl" />
  </Center>
);

interface AuthenticatedJoinFlowProps {
  challenge: Challenge;
}

function AuthenticatedJoinFlow({challenge}: AuthenticatedJoinFlowProps) {
  const {replace, query} = useRouter();
  const authDisclosure = useDisclosure();
  const buyCoinsDisclosure = useDisclosure();
  const verifyNumberDisclosure = useDisclosure();
  const [canJoinChallenge, setCanJoinChallenge] = useBoolean(false);

  const meQuery = useMeQuery();
  const regionSettingsQuery = useRegionSettings();

  const onJoinChallengeModalClose = async (onDisclosureClose?: () => void) => {
    delete query['challenge-id'];
    await replace({query}, undefined, {shallow: true, scroll: false});

    setCanJoinChallenge.off();

    onDisclosureClose?.();
  };

  const onAuthModalClose = (me: Me | undefined) => {
    authDisclosure.onClose();

    if (!me) return onJoinChallengeModalClose();

    onAuthedChallengeOpen(me);
  };

  const onAuthedChallengeOpen = (me: Me) => {
    if (regionSettingsQuery.data?.settings.phone_number_verification_enabled && !me.phone_verified)
      return verifyNumberDisclosure.onOpen();

    if (me.coins < challenge.entry_fee) return buyCoinsDisclosure.onOpen();

    setCanJoinChallenge.on();
  };

  useEffect(() => {
    if (meQuery.fetchStatus !== 'idle' && meQuery.isLoading) return;

    if (!meQuery.isSuccess) return authDisclosure.onOpen();

    onAuthedChallengeOpen(meQuery.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meQuery.isSuccess]);

  if (meQuery.fetchStatus !== 'idle' && meQuery.isLoading) return <LoadingOverlay />;

  if (!canJoinChallenge) {
    return (
      <>
        <AuthModal {...authDisclosure} onClose={onAuthModalClose} />

        <VerifyNumberToJoin
          {...verifyNumberDisclosure}
          onClose={() => onJoinChallengeModalClose(verifyNumberDisclosure.onClose)}
        />

        <BuyCoinsModal
          {...buyCoinsDisclosure}
          onClose={() => onJoinChallengeModalClose(buyCoinsDisclosure.onClose)}
          fees={challenge.entry_fee ?? 0}
        />
      </>
    );
  }

  return <JoinModal challenge={challenge} onClose={onJoinChallengeModalClose} />;
}
